import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const ShaftSizeSelector = ({
  initialDimensions = { width: 1800, depth: 1900, height: 3000 },
  liftType,
  buildingType,
  floors,
  onChange,
  onContinue,
  onBack,
}) => {
  // State for dimensions
  const [dimensions, setDimensions] = useState(initialDimensions);
  const [presetSize, setPresetSize] = useState("custom");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [activeTab, setActiveTab] = useState("presets");

  const recommendedDimensions = {
    mrl: {
      small: {
        width: 1250,
        depth: 1250,
        height: 3000,
        label: "Compact 4-6 Person",
      },
      medium: {
        width: 1550,
        depth: 1550,
        height: 3400,
        label: "Standard 6 Person",
      },
      large: {
        width: 1850,
        depth: 1850,
        height: 3800,
        label: "Large 8 Person",
      },
      minWidth: 1600,
      maxWidth: 2800,
      minDepth: 1700,
      maxDepth: 3000,
      minHeight: 2800,
      maxHeight: 4200,
    },
    hydraulic: {
      small: {
        width: 1250,
        depth: 1250,
        height: 3000,
        label: "Compact 4-6 Person",
      },
      medium: {
        width: 1550,
        depth: 1550,
        height: 3400,
        label: "Standard 6 Person",
      },
      large: {
        width: 1850,
        depth: 1850,
        height: 3800,
        label: "Large 8 Person",
      },
      minWidth: 1600,
      maxWidth: 2800,
      minDepth: 1700,
      maxDepth: 3000,
      minHeight: 2800,
      maxHeight: 4200,
    },
    dumbwaiter: {
      small: {
        width: 1250,
        depth: 1250,
        height: 3000,
        label: "Compact 4-6 Person",
      },
      medium: {
        width: 1550,
        depth: 1550,
        height: 3400,
        label: "Standard 6 Person",
      },
      large: {
        width: 1850,
        depth: 1850,
        height: 3800,
        label: "Large 8 Person",
      },
      minWidth: 1600,
      maxWidth: 2800,
      minDepth: 1700,
      maxDepth: 3000,
      minHeight: 2800,
      maxHeight: 4200,
    },
    "car-lift": {
      small: {
        width: 2400,
        depth: 5500,
        height: 2200,
        label: "Standard Car Lift",
      },
      medium: {
        width: 2800,
        depth: 6000,
        height: 2400,
        label: "Large Vehicle Lift",
      },
      large: {
        width: 3200,
        depth: 6500,
        height: 2600,
        label: "SUV/Truck Lift",
      },
      minWidth: 2200,
      maxWidth: 3600,
      minDepth: 5000,
      maxDepth: 7000,
      minHeight: 2000,
      maxHeight: 3000,
    },
    default: {
      small: { width: 1800, depth: 1900, height: 3000, label: "Compact" },
      medium: { width: 2100, depth: 2200, height: 3400, label: "Standard" },
      large: { width: 2400, depth: 2500, height: 3800, label: "Large" },
      minWidth: 1600,
      maxWidth: 2800,
      minDepth: 1700,
      maxDepth: 3000,
      minHeight: 2800,
      maxHeight: 4200,
    },
  };

  // Get current recommendations based on selected lift type
  const currentRecommendations =
    recommendedDimensions[liftType] || recommendedDimensions.default;

  // Update parent component with dimension changes
  useEffect(() => {
    // Deep compare initialDimensions with current dimensions
    const isDifferent =
      initialDimensions.width !== dimensions.width ||
      initialDimensions.depth !== dimensions.depth ||
      initialDimensions.height !== dimensions.height;

    // Only update state if values are actually different
    if (isDifferent) {
      setDimensions(initialDimensions);
    }
  }, [initialDimensions]); // Only runs when initialDimensions prop changes

  // Modify the existing effect that updates the parent
  useEffect(() => {
    // Track whether this change was triggered by props or user interaction
    // Only call onChange if dimensions are different from initialDimensions
    const isDifferent =
      dimensions.width !== initialDimensions.width ||
      dimensions.depth !== initialDimensions.depth ||
      dimensions.height !== initialDimensions.height;

    if (onChange && isDifferent) {
      onChange(dimensions);
    }
  }, [dimensions, onChange, initialDimensions]);

  useEffect(() => {
    // Check if initialDimensions match any preset
    const currentRecs =
      recommendedDimensions[liftType] || recommendedDimensions.default;

    if (
      initialDimensions.width === currentRecs.small.width &&
      initialDimensions.depth === currentRecs.small.depth &&
      initialDimensions.height === currentRecs.small.height
    ) {
      setPresetSize("small");
    } else if (
      initialDimensions.width === currentRecs.medium.width &&
      initialDimensions.depth === currentRecs.medium.depth &&
      initialDimensions.height === currentRecs.medium.height
    ) {
      setPresetSize("medium");
    } else if (
      initialDimensions.width === currentRecs.large.width &&
      initialDimensions.depth === currentRecs.large.depth &&
      initialDimensions.height === currentRecs.large.height
    ) {
      setPresetSize("large");
    } else {
      setPresetSize("custom");
    }
  }, []);

  // Handle dimension changes with validation
  const handleDimensionChange = (dimension, value) => {
    const numValue = parseInt(value, 10) || 0;
    let validatedValue = numValue;

    // Apply min/max constraints
    if (dimension === "width") {
      validatedValue = Math.min(
        Math.max(numValue, currentRecommendations.minWidth),
        currentRecommendations.maxWidth
      );
    } else if (dimension === "depth") {
      validatedValue = Math.min(
        Math.max(numValue, currentRecommendations.minDepth),
        currentRecommendations.maxDepth
      );
    } else if (dimension === "height") {
      validatedValue = Math.min(
        Math.max(numValue, currentRecommendations.minHeight),
        currentRecommendations.maxHeight
      );
    }

    setDimensions((prev) => ({ ...prev, [dimension]: validatedValue }));

    // Reset preset selection to custom if values are changed manually
    if (
      presetSize !== "custom" &&
      ((dimension === "width" &&
        validatedValue !== currentRecommendations[presetSize].width) ||
        (dimension === "depth" &&
          validatedValue !== currentRecommendations[presetSize].depth) ||
        (dimension === "height" &&
          validatedValue !== currentRecommendations[presetSize].height))
    ) {
      setPresetSize("custom");
    }
  };

  // Handle preset selection
  const handlePresetChange = (preset) => {
    if (preset === "custom") {
      setPresetSize("custom");
      return;
    }

    const newDimensions = currentRecommendations[preset];
    setPresetSize(preset);
    setDimensions({
      width: newDimensions.width,
      depth: newDimensions.depth,
      height: newDimensions.height,
    });
  };

  // Format dimension for display in mm with commas
  const formatDimension = (value) => {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Calculate scale factor based on dimensions to fit visualization properly
  const calculateScaleFactor = () => {
    const maxDimension = Math.max(dimensions.width, dimensions.depth);
    return Math.min(250 / maxDimension, 0.15);
  };

  // Format dimensions for display
  const scaleFactor = calculateScaleFactor();
  const elevatorWidth = dimensions.width * scaleFactor;
  const elevatorDepth = dimensions.depth * scaleFactor;

  // Helper to get lift type name
  const getLiftTypeName = () => {
    const liftNames = {
      mrl: "Machine Room-Less (MRL)",
      hydraulic: "Hydraulic",
      goods: "Goods Elevator",
      panoramic: "Panoramic/Glass",
    };

    return liftNames[liftType] || "Custom Elevator";
  };

  // Get appropriate icon for lift type
  const getLiftTypeIcon = () => {
    const icons = {
      mrl: "elevator",
      hydraulic: "tachometer-alt",
      dumbwaiter: "utensils",
      "car-lift": "car",
    };

    return icons[liftType] || "elevator";
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>
          <i
            className={`fas fa-${getLiftTypeIcon()}`}
            style={styles.titleIcon}
          ></i>
          Configure Shaft Dimensions
        </h2>
        <p style={styles.subtitle}>
          {getLiftTypeName()} • {buildingType} building • {floors} floors
        </p>
      </div>

      {/* Tab navigation - visible on all screens */}
      <div style={styles.tabNav}>
        {/* <button
          style={{
            ...styles.tabButton,
            ...(activeTab === "visualization" ? styles.activeTab : {}),
          }}
          onClick={() => setActiveTab("visualization")}
        >
          <i className="fas fa-eye"></i>
          <span style={styles.tabText}>Preview</span>
        </button> */}
        <button
          style={{
            ...styles.tabButton,
            ...(activeTab === "presets" ? styles.activeTab : {}),
          }}
          onClick={() => setActiveTab("presets")}
        >
          <i className="fas fa-th-large"></i>
          <span style={styles.tabText}>Presets</span>
        </button>
        <button
          style={{
            ...styles.tabButton,
            ...(activeTab === "dimensions" ? styles.activeTab : {}),
          }}
          onClick={() => setActiveTab("dimensions")}
        >
          <i className="fas fa-sliders-h"></i>
          <span style={styles.tabText}>Adjust</span>
        </button>
      </div>

      {/* Tab content */}
      <div style={styles.tabContent}>
        {/* Visualization panel */}
        {activeTab === "visualization" && (
          <div style={styles.panel}>
            <div style={styles.visualizerContainer}>
              <div style={styles.previewBox}>
                <div
                  style={{
                    ...styles.shaft3D,
                    width: elevatorWidth + 40,
                    height: Math.min(
                      280,
                      dimensions.height * scaleFactor * 0.5
                    ),
                    transform: `rotateX(55deg) rotateZ(-45deg)`,
                  }}
                >
                  {/* Back wall */}
                  <div
                    style={{
                      ...styles.shaftWall,
                      ...styles.wallBack,
                      width: elevatorWidth + 40,
                      height: Math.min(
                        280,
                        dimensions.height * scaleFactor * 0.5
                      ),
                    }}
                  ></div>

                  {/* Left wall */}
                  <div
                    style={{
                      ...styles.shaftWall,
                      ...styles.wallLeft,
                      width: elevatorDepth + 40,
                      height: Math.min(
                        280,
                        dimensions.height * scaleFactor * 0.5
                      ),
                    }}
                  ></div>

                  {/* Bottom */}
                  <div
                    style={{
                      ...styles.shaftWall,
                      ...styles.wallBottom,
                      width: elevatorWidth + 40,
                      height: elevatorDepth + 40,
                    }}
                  >
                    {/* Elevator car */}
                    <div
                      style={{
                        ...styles.elevatorCar,
                        width: elevatorWidth - 20,
                        height: elevatorDepth - 20,
                      }}
                    >
                      {liftType === "panoramic" && (
                        <i
                          className="fas fa-mountain"
                          style={styles.carIcon}
                        ></i>
                      )}
                      {liftType === "goods" && (
                        <i className="fas fa-box" style={styles.carIcon}></i>
                      )}
                      {liftType === "mrl" && (
                        <i
                          className="fas fa-elevator"
                          style={styles.carIcon}
                        ></i>
                      )}
                      {liftType === "hydraulic" && (
                        <i
                          className="fas fa-tachometer-alt"
                          style={styles.carIcon}
                        ></i>
                      )}
                    </div>
                  </div>

                  {/* Dimension arrows and labels */}
                  <div style={styles.dimensionIndicators}>
                    {/* Width arrow */}
                    <div style={styles.widthArrow}>
                      <div style={styles.arrow}></div>
                      <div style={styles.dimensionLine}></div>
                      <div
                        style={styles.arrow}
                        style={{ transform: "rotate(180deg)" }}
                      ></div>
                    </div>

                    {/* Depth arrow */}
                    <div style={styles.depthArrow}>
                      <div style={styles.arrow}></div>
                      <div style={styles.dimensionLine}></div>
                      <div
                        style={styles.arrow}
                        style={{ transform: "rotate(180deg)" }}
                      ></div>
                    </div>

                    {/* Height arrow */}
                    <div style={styles.heightArrow}>
                      <div style={styles.arrow}></div>
                      <div style={styles.dimensionLine}></div>
                      <div
                        style={styles.arrow}
                        style={{ transform: "rotate(180deg)" }}
                      ></div>
                    </div>
                  </div>

                  {/* Dimension labels */}
                  <div style={styles.dimensionLabels}>
                    <div style={styles.widthLabel}>
                      <span style={styles.labelText}>
                        Width: {formatDimension(dimensions.width)} mm
                      </span>
                    </div>
                    <div style={styles.depthLabel}>
                      <span style={styles.labelText}>
                        Depth: {formatDimension(dimensions.depth)} mm
                      </span>
                    </div>
                    <div style={styles.heightLabel}>
                      <span style={styles.labelText}>
                        Height: {formatDimension(dimensions.height)} mm
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Simple dimension summary */}
            <div style={styles.summary}>
              <div style={styles.dimensionSummary}>
                <div style={styles.dimensionItem}>
                  <i className="fas fa-arrows-alt-h"></i> Width:{" "}
                  {formatDimension(dimensions.width)} mm
                </div>
                <div style={styles.dimensionItem}>
                  <i className="fas fa-arrows-alt-v"></i> Depth:{" "}
                  {formatDimension(dimensions.depth)} mm
                </div>
                <div style={styles.dimensionItem}>
                  <i className="fas fa-ruler-vertical"></i> Height:{" "}
                  {formatDimension(dimensions.height)} mm
                </div>
              </div>

              <div style={styles.configBadge}>
                {presetSize !== "custom" ? (
                  <span>{currentRecommendations[presetSize].label}</span>
                ) : (
                  <span>Custom Size</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Presets panel */}
        {activeTab === "presets" && (
          <div style={styles.panel}>
            <p style={styles.panelDescription}>
              Select a standard configuration for your {getLiftTypeName()}:
            </p>

            <div style={styles.presets}>
              <div
                style={{
                  ...styles.presetOption,
                  ...(presetSize === "small" ? styles.selectedPreset : {}),
                }}
                onClick={() => handlePresetChange("small")}
              >
                <div style={styles.presetVisual}>
                  <div style={styles.presetShaft}></div>
                </div>
                <div style={styles.presetInfo}>
                  <h4 style={styles.presetName}>Compact</h4>
                  <p style={styles.presetDescription}>
                    {currentRecommendations.small.label}
                  </p>
                  <p style={styles.presetSize}>
                    {formatDimension(currentRecommendations.small.width)} ×{" "}
                    {formatDimension(currentRecommendations.small.depth)} mm
                  </p>
                </div>
                {presetSize === "small" && (
                  <i className="fas fa-check" style={styles.presetCheck}></i>
                )}
              </div>

              <div
                style={{
                  ...styles.presetOption,
                  ...(presetSize === "medium" ? styles.selectedPreset : {}),
                }}
                onClick={() => handlePresetChange("medium")}
              >
                <div style={styles.presetVisual}>
                  <div
                    style={{
                      ...styles.presetShaft,
                      width: "60%",
                      height: "60%",
                    }}
                  ></div>
                </div>
                <div style={styles.presetInfo}>
                  <h4 style={styles.presetName}>Standard</h4>
                  <p style={styles.presetDescription}>
                    {currentRecommendations.medium.label}
                  </p>
                  <p style={styles.presetSize}>
                    {formatDimension(currentRecommendations.medium.width)} ×{" "}
                    {formatDimension(currentRecommendations.medium.depth)} mm
                  </p>
                </div>
                {presetSize === "medium" && (
                  <i className="fas fa-check" style={styles.presetCheck}></i>
                )}
              </div>

              <div
                style={{
                  ...styles.presetOption,
                  ...(presetSize === "large" ? styles.selectedPreset : {}),
                }}
                onClick={() => handlePresetChange("large")}
              >
                <div style={styles.presetVisual}>
                  <div
                    style={{
                      ...styles.presetShaft,
                      width: "70%",
                      height: "70%",
                    }}
                  ></div>
                </div>
                <div style={styles.presetInfo}>
                  <h4 style={styles.presetName}>Large</h4>
                  <p style={styles.presetDescription}>
                    {currentRecommendations.large.label}
                  </p>
                  <p style={styles.presetSize}>
                    {formatDimension(currentRecommendations.large.width)} ×{" "}
                    {formatDimension(currentRecommendations.large.depth)} mm
                  </p>
                </div>
                {presetSize === "large" && (
                  <i className="fas fa-check" style={styles.presetCheck}></i>
                )}
              </div>

              <div
                style={{
                  ...styles.presetOption,
                  ...styles.customOption,
                  ...(presetSize === "custom" ? styles.selectedPreset : {}),
                }}
                onClick={() => handlePresetChange("custom")}
              >
                <i className="fas fa-sliders-h" style={styles.customIcon}></i>
                <div style={styles.presetInfo}>
                  <h4 style={styles.presetName}>Custom Size</h4>
                  <p style={styles.presetDescription}>
                    Adjust to your specific requirements
                  </p>
                </div>
                {presetSize === "custom" && (
                  <i className="fas fa-check" style={styles.presetCheck}></i>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Dimensions panel */}
        {activeTab === "dimensions" && (
          <div style={styles.panel}>
            <div style={styles.controls}>
              {/* Width control */}
              <div style={styles.control}>
                <label style={styles.controlLabel}>
                  <i className="fas fa-arrows-alt-h"></i> Width:
                  <span style={styles.rangeLabel}>
                    ({formatDimension(currentRecommendations.minWidth)} -{" "}
                    {formatDimension(currentRecommendations.maxWidth)} mm)
                  </span>
                </label>

                <div style={styles.sliderContainer}>
                  <button
                    style={styles.controlButton}
                    onClick={() =>
                      handleDimensionChange("width", dimensions.width - 50)
                    }
                    disabled={
                      dimensions.width <= currentRecommendations.minWidth
                    }
                  >
                    <i className="fas fa-minus"></i>
                  </button>

                  <input
                    type="range"
                    min={currentRecommendations.minWidth}
                    max={currentRecommendations.maxWidth}
                    step="50"
                    value={dimensions.width}
                    onChange={(e) =>
                      handleDimensionChange("width", e.target.value)
                    }
                    style={styles.slider}
                  />

                  <button
                    style={styles.controlButton}
                    onClick={() =>
                      handleDimensionChange("width", dimensions.width + 50)
                    }
                    disabled={
                      dimensions.width >= currentRecommendations.maxWidth
                    }
                  >
                    <i className="fas fa-plus"></i>
                  </button>
                </div>

                <div style={styles.valueDisplay}>
                  <input
                    type="number"
                    value={dimensions.width}
                    onChange={(e) =>
                      handleDimensionChange("width", e.target.value)
                    }
                    style={styles.inputValue}
                  />
                  <span style={styles.unitText}>mm</span>
                </div>
              </div>

              {/* Depth control */}
              <div style={styles.control}>
                <label style={styles.controlLabel}>
                  <i className="fas fa-arrows-alt-v"></i> Depth:
                  <span style={styles.rangeLabel}>
                    ({formatDimension(currentRecommendations.minDepth)} -{" "}
                    {formatDimension(currentRecommendations.maxDepth)} mm)
                  </span>
                </label>

                <div style={styles.sliderContainer}>
                  <button
                    style={styles.controlButton}
                    onClick={() =>
                      handleDimensionChange("depth", dimensions.depth - 50)
                    }
                    disabled={
                      dimensions.depth <= currentRecommendations.minDepth
                    }
                  >
                    <i className="fas fa-minus"></i>
                  </button>

                  <input
                    type="range"
                    min={currentRecommendations.minDepth}
                    max={currentRecommendations.maxDepth}
                    step="50"
                    value={dimensions.depth}
                    onChange={(e) =>
                      handleDimensionChange("depth", e.target.value)
                    }
                    style={styles.slider}
                  />

                  <button
                    style={styles.controlButton}
                    onClick={() =>
                      handleDimensionChange("depth", dimensions.depth + 50)
                    }
                    disabled={
                      dimensions.depth >= currentRecommendations.maxDepth
                    }
                  >
                    <i className="fas fa-plus"></i>
                  </button>
                </div>

                <div style={styles.valueDisplay}>
                  <input
                    type="number"
                    value={dimensions.depth}
                    onChange={(e) =>
                      handleDimensionChange("depth", e.target.value)
                    }
                    style={styles.inputValue}
                  />
                  <span style={styles.unitText}>mm</span>
                </div>
              </div>

              {/* Height toggle button */}
              <button
                style={styles.toggleButton}
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                {showAdvanced ? "Hide Height Options" : "Show Height Options"}
                <i
                  className={`fas fa-chevron-${showAdvanced ? "up" : "down"}`}
                  style={styles.toggleIcon}
                ></i>
              </button>

              {/* Height control (collapsible) */}
              {showAdvanced && (
                <div style={styles.control}>
                  <label style={styles.controlLabel}>
                    <i className="fas fa-ruler-vertical"></i> Height:
                    <span style={styles.rangeLabel}>
                      ({formatDimension(currentRecommendations.minHeight)} -{" "}
                      {formatDimension(currentRecommendations.maxHeight)} mm)
                    </span>
                  </label>

                  <div style={styles.sliderContainer}>
                    <button
                      style={styles.controlButton}
                      onClick={() =>
                        handleDimensionChange("height", dimensions.height - 100)
                      }
                      disabled={
                        dimensions.height <= currentRecommendations.minHeight
                      }
                    >
                      <i className="fas fa-minus"></i>
                    </button>

                    <input
                      type="range"
                      min={currentRecommendations.minHeight}
                      max={currentRecommendations.maxHeight}
                      step="100"
                      value={dimensions.height}
                      onChange={(e) =>
                        handleDimensionChange("height", e.target.value)
                      }
                      style={styles.slider}
                    />

                    <button
                      style={styles.controlButton}
                      onClick={() =>
                        handleDimensionChange("height", dimensions.height + 100)
                      }
                      disabled={
                        dimensions.height >= currentRecommendations.maxHeight
                      }
                    >
                      <i className="fas fa-plus"></i>
                    </button>
                  </div>

                  <div style={styles.valueDisplay}>
                    <input
                      type="number"
                      value={dimensions.height}
                      onChange={(e) =>
                        handleDimensionChange("height", e.target.value)
                      }
                      style={styles.inputValue}
                    />
                    <span style={styles.unitText}>mm</span>
                  </div>
                </div>
              )}

              <div style={styles.infoSection}>
                <p style={styles.infoText}>
                  <i className="fas fa-info-circle"></i> Width and depth
                  determine elevator car capacity. Height affects travel speed
                  and overhead requirements.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation buttons */}
      <div style={styles.navigation}>
        <button onClick={onBack} style={styles.backButton}>
          <i className="fas fa-arrow-left"></i> Back
        </button>

        <button onClick={onContinue} style={styles.nextButton}>
          Continue <i className="fas fa-arrow-right"></i>
        </button>
      </div>
    </div>
  );
};

// Modern streamlined design with better responsiveness
const styles = {
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "20px",
    minWidth: "320px",
    overflow: "hidden",
  },
  header: {
    marginBottom: "30px",
  },
  title: {
    fontSize: "28px",
    fontWeight: "700",
    margin: "0 0 6px",
    color: "#1d1d1f",
  },
  titleIcon: {
    marginRight: "8px",
    color: "#007aff",
  },
  subtitle: {
    fontSize: "16px",
    color: "#4d4d4d",
    margin: 0,
  },
  tabNav: {
    display: "flex",
    borderRadius: "12px",
    overflow: "hidden",
    marginBottom: "20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    backgroundColor: "#f5f5f7",
  },
  tabButton: {
    flex: 1,
    padding: "14px 10px",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "6px",
    fontSize: "14px",
    fontWeight: "500",
    color: "#4d4d4d",
    transition: "all 0.2s ease",
  },
  tabText: {
    display: "block",
    "@media (max-width: 480px)": {
      display: "none",
    },
  },
  activeTab: {
    backgroundColor: "#ffffff",
    color: "#007aff",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  },
  tabContent: {
    marginBottom: "30px",
  },
  panel: {
    backgroundColor: "#ffffff",
    borderRadius: "15px",
    padding: "20px",
    boxShadow: "0 5px 20px rgba(0,0,0,0.06)",
  },
  visualizerContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "350px",
    padding: "20px 0",
    overflowX: "hidden",
    position: "relative",
    "@media (max-width: 768px)": {
      minHeight: "300px",
      padding: "10px 0",
    },
  },
  previewBox: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    perspective: "1200px",
    marginTop: "10px",
    marginBottom: "30px",
    width: "100%",
    height: "400px",
    "@media (max-width: 768px)": {
      height: "250px",
    },
  },
  shaft3D: {
    position: "relative",
    transformStyle: "preserve-3d",
    transition: "all 0.5s ease",
    transform: "rotateX(55deg) rotateZ(-45deg)",
  },
  shaftWall: {
    position: "absolute",
    borderRadius: "6px",
    transition: "all 0.3s ease",
  },
  wallBack: {
    transformOrigin: "bottom",
    transform: "rotateX(90deg)",
    top: 10,
    left: 0,
    background: "linear-gradient(to top, #d4d4d8, #e4e4e8)",
    boxShadow: "inset 0 0 20px rgba(0,0,0,0.15)",
    borderBottom: "2px solid #aaa",
  },
  wallLeft: {
    transformOrigin: "left bottom",
    transform: "rotateX(90deg) rotateY(90deg)",
    top: -285,
    right: -30,
    background: "linear-gradient(to top, #c8c8d0, #d8d8e0)",
    boxShadow: "inset 0 0 20px rgba(0,0,0,0.2)",
    borderBottom: "2px solid #aaa",
  },
  wallBottom: {
    bottom: 0,
    left: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "linear-gradient(45deg, #e0e0e0, #f0f0f0)",
    boxShadow: "inset 0 0 30px rgba(0,0,0,0.1), 0 10px 20px rgba(0,0,0,0.05)",
    border: "1px solid #ccc",
  },
  elevatorCar: {
    background: "linear-gradient(135deg, #0071e6, #00a2ff)",
    borderRadius: "5px",
    boxShadow: "0 8px 16px rgba(0,114,255,0.3)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    transition: "all 0.3s ease",
    transform: "translateZ(5px)",
  },
  carIcon: {
    color: "rgba(255,255,255,0.9)",
    fontSize: "22px",
    textShadow: "0 2px 4px rgba(0,0,0,0.2)",
  },
  dimensionIndicators: {
    position: "absolute",
    width: "100%",
    height: "100%",
    top: 0,
    left: 0,
    pointerEvents: "none",
  },
  widthArrow: {
    position: "absolute",
    bottom: "-30px",
    left: "10%",
    width: "80%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  depthArrow: {
    position: "absolute",
    right: "-20px",
    bottom: "10%",
    height: "80%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-between",
    transform: "rotate(90deg) translateX(-50%)",
    transformOrigin: "left center",
  },
  heightArrow: {
    position: "absolute",
    left: "-20px",
    bottom: "10%",
    height: "80%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-between",
    transform: "rotate(-90deg) translateX(50%)",
    transformOrigin: "right center",
  },
  arrow: {
    width: 0,
    height: 0,
    borderLeft: "5px solid transparent",
    borderRight: "5px solid transparent",
    borderBottom: "8px solid #007aff",
  },
  dimensionLine: {
    flex: 1,
    width: "2px",
    backgroundColor: "#007aff",
    margin: "0 4px",
  },
  dimensionLabels: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    pointerEvents: "none",
  },
  widthLabel: {
    position: "absolute",
    bottom: "-50px",
    left: "50%",
    transform: "translateX(-50%)",
    whiteSpace: "nowrap",
    backgroundColor: "rgba(255,255,255,0.95)",
    padding: "4px 10px",
    borderRadius: "6px",
    boxShadow: "0 3px 8px rgba(0,0,0,0.1)",
    border: "1px solid #e0e0e0",
  },
  depthLabel: {
    position: "absolute",
    bottom: "50%",
    right: "-110px",
    transform: "translateY(50%)",
    whiteSpace: "nowrap",
    backgroundColor: "rgba(255,255,255,0.95)",
    padding: "4px 10px",
    borderRadius: "6px",
    boxShadow: "0 3px 8px rgba(0,0,0,0.1)",
    border: "1px solid #e0e0e0",
    "@media (max-width: 768px)": {
      right: "-90px",
      padding: "3px 6px",
    },
  },
  heightLabel: {
    position: "absolute",
    bottom: "50%",
    left: "-110px",
    transform: "translateY(50%)",
    whiteSpace: "nowrap",
    backgroundColor: "rgba(255,255,255,0.95)",
    padding: "4px 10px",
    borderRadius: "6px",
    boxShadow: "0 3px 8px rgba(0,0,0,0.1)",
    border: "1px solid #e0e0e0",
    "@media (max-width: 768px)": {
      left: "-90px",
      padding: "3px 6px",
    },
  },
  labelText: {
    fontSize: "13px",
    fontWeight: "500",
    color: "#333",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    "@media (max-width: 768px)": {
      fontSize: "11px",
    },
  },
  summary: {
    backgroundColor: "#f9f9fb",
    padding: "15px",
    borderRadius: "10px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "10px",
  },
  dimensionSummary: {
    display: "flex",
    flexWrap: "wrap",
    gap: "15px",
  },
  dimensionItem: {
    fontSize: "14px",
    color: "#333",
    display: "flex",
    alignItems: "center",
    gap: "5px",
  },
  configBadge: {
    backgroundColor: "#007aff",
    color: "white",
    padding: "5px 12px",
    borderRadius: "20px",
    fontSize: "13px",
    fontWeight: "500",
    boxShadow: "0 3px 8px rgba(0,122,255,0.2)",
  },
  panelDescription: {
    margin: "0 0 20px",
    color: "#4d4d4d",
    fontSize: "15px",
  },
  presets: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
    gap: "15px",
    "@media (max-width: 600px)": {
      gridTemplateColumns: "1fr",
    },
  },
  presetOption: {
    display: "flex",
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "15px",
    boxShadow: "0 3px 10px rgba(0,0,0,0.05)",
    border: "2px solid transparent",
    cursor: "pointer",
    position: "relative",
    transition: "all 0.2s ease",
    "&:hover": {
      boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
      transform: "translateY(-2px)",
    },
  },
  selectedPreset: {
    borderColor: "#007aff",
    boxShadow: "0 5px 15px rgba(0,122,255,0.15)",
  },
  presetVisual: {
    width: "60px",
    height: "60px",
    backgroundColor: "#f5f5f7",
    borderRadius: "8px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginRight: "15px",
  },
  presetShaft: {
    width: "50%",
    height: "50%",
    backgroundColor: "#007aff",
    borderRadius: "3px",
  },
  presetInfo: {
    flex: 1,
  },
  presetName: {
    margin: "0 0 3px",
    fontSize: "16px",
    fontWeight: "600",
  },
  presetDescription: {
    margin: "0 0 5px",
    fontSize: "13px",
    color: "#4d4d4d",
  },
  presetSize: {
    margin: 0,
    fontSize: "12px",
    color: "#777",
  },
  customOption: {
    backgroundColor: "#f5f5f7",
    display: "flex",
    alignItems: "center",
  },
  customIcon: {
    fontSize: "24px",
    color: "#007aff",
    marginRight: "15px",
  },
  presetCheck: {
    position: "absolute",
    top: "10px",
    right: "10px",
    color: "#007aff",
    fontSize: "16px",
  },
  controls: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  control: {
    backgroundColor: "#f9f9fb",
    borderRadius: "10px",
    padding: "16px",
  },
  controlLabel: {
    display: "flex",
    alignItems: "center",
    fontSize: "15px",
    fontWeight: "500",
    color: "#333",
    marginBottom: "12px",
    gap: "5px",
  },
  rangeLabel: {
    fontSize: "12px",
    color: "#777",
    fontWeight: "400",
    marginLeft: "auto",
  },
  sliderContainer: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "10px",
  },
  slider: {
    flex: 1,
    height: "4px",
    WebkitAppearance: "none",
    appearance: "none",
    backgroundColor: "#e0e0e0",
    outline: "none",
    borderRadius: "2px",
    "&::-webkit-slider-thumb": {
      WebkitAppearance: "none",
      appearance: "none",
      width: "18px",
      height: "18px",
      borderRadius: "50%",
      backgroundColor: "#007aff",
      cursor: "pointer",
      boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
    },
  },
  controlButton: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    border: "none",
    backgroundColor: "white",
    color: "#007aff",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    boxShadow: "0 3px 8px rgba(0,0,0,0.08)",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#f5f5f7",
    },
    "&:disabled": {
      color: "#ccc",
      cursor: "not-allowed",
      boxShadow: "none",
    },
  },
  valueDisplay: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
  },
  inputValue: {
    width: "90px",
    padding: "8px",
    borderRadius: "6px",
    border: "1px solid #e0e0e0",
    fontSize: "14px",
    textAlign: "right",
  },
  unitText: {
    color: "#777",
    fontSize: "14px",
  },
  toggleButton: {
    backgroundColor: "#f5f5f7",
    border: "none",
    borderRadius: "8px",
    padding: "12px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    fontSize: "14px",
    color: "#4d4d4d",
    fontWeight: "500",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#e9e9eb",
    },
  },
  toggleIcon: {
    fontSize: "12px",
  },
  infoSection: {
    backgroundColor: "#f0f7ff",
    borderRadius: "10px",
    padding: "12px",
    marginTop: "10px",
  },
  infoText: {
    margin: 0,
    fontSize: "13px",
    color: "#0055b3",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  navigation: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "30px",
  },
  backButton: {
    padding: "12px 24px",
    borderRadius: "8px",
    border: "1px solid #d0d0d0",
    backgroundColor: "white",
    color: "#4d4d4d",
    fontSize: "15px",
    fontWeight: "500",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#f5f5f7",
    },
  },
  nextButton: {
    padding: "12px 24px",
    borderRadius: "8px",
    border: "none",
    backgroundColor: "#007aff",
    color: "white",
    fontSize: "15px",
    fontWeight: "500",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    boxShadow: "0 4px 12px rgba(0,122,255,0.2)",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#0062cc",
    },
  },
};

export default ShaftSizeSelector;
